import { useContext } from 'react';
import { useMutation, useSuspenseQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { t } from '@lingui/core/macro';
import { SidebarContext } from '@/common/components/sidebar/SidebarContext';
import { ProductService } from '../../services/ProductService';
import { productQueries, productQueryKeys } from '../../queries';
import {
  billingAddressQueries,
  billingAddressQueryKeys,
} from '@/features/billing-addresses/queries';
import { organizationQueries } from '@/features/organizations/queries';
import type { ActivateInput } from '../product-activation-form/ProductActivationForm';

export type DependencyInfo = {
  productTypeName: string;
  isActive: boolean;
};

export function useProductPanel(
  variantId: number,
  supplyPointId: number,
  canManageProducts: boolean
) {
  const queryClient = useQueryClient();
  const { onClose } = useContext(SidebarContext);

  const { data: variants } = useSuspenseQuery(productQueries.variants());
  const { data: products } = useSuspenseQuery(productQueries.products(supplyPointId));
  const { data: billingAddresses } = useSuspenseQuery(billingAddressQueries.list());
  // Needed for the scope select of the billing address dialog opened from the activation form.
  const { data: orgs } = useSuspenseQuery(organizationQueries.list());

  const variant = variants.find((v) => v.id === variantId) ?? null;

  const variantsForProductType = variants.filter(
    (v) => v.productType.name === variant?.productType.name
  );

  // Active if any variant of this product type has an active product
  const activeProduct =
    products.find((p) => variantsForProductType.some((v) => v.id === p.variant)) ?? null;
  const isActive = !!activeProduct;

  // Resolve each dependency variant ID to its product type name and active status
  const dependencies: DependencyInfo[] = (variant?.productType.dependencies ?? []).map(
    (depVariantId) => {
      const depVariant = variants.find((v) => v.id === depVariantId);
      const depProductTypeName = depVariant?.productType.name ?? String(depVariantId);
      const depVariantIds = variants
        .filter((v) => v.productType.name === depProductTypeName)
        .map((v) => v.id);
      const isDepActive = products.some((p) => depVariantIds.includes(p.variant));
      return { productTypeName: depProductTypeName, isActive: isDepActive };
    }
  );

  const invalidateProducts = () =>
    queryClient.invalidateQueries({ queryKey: productQueryKeys.products(supplyPointId) });

  const invalidateBillingAddresses = () =>
    queryClient.invalidateQueries({ queryKey: billingAddressQueryKeys.all });

  const cancelMutation = useMutation({
    mutationFn: () => {
      if (!canManageProducts) throw new Error('Not authorized to manage products');
      if (!activeProduct) throw new Error('No active product to cancel');
      return ProductService.deleteProduct(activeProduct.id);
    },
    onSuccess: async () => {
      await invalidateProducts();
      toast.success(t`Product deactivated`);
      onClose?.();
    },
    onError: () => toast.error(t`Failed to deactivate product`),
  });

  const activateMutation = useMutation({
    mutationFn: (input: ActivateInput) => {
      if (!canManageProducts) throw new Error('Not authorized to manage products');
      return ProductService.createProduct({
        variant: input.selectedVariantId,
        system: supplyPointId,
        billingContact: input.billingContact,
        billingFrequency: input.billingFrequency,
      });
    },
    onSuccess: async () => {
      await invalidateProducts();
      toast.success(t`Product activated successfully`);
      onClose?.();
    },
    onError: () => toast.error(t`Failed to activate product`),
  });

  return {
    variant,
    variantsForProductType,
    activeProduct,
    dependencies,
    billingAddresses,
    orgs,
    invalidateBillingAddresses,
    isActive,
    onClose,
    activateMutation,
    cancelMutation,
  };
}
